import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useMemo } from "react";
import { Compare } from "@emberly/zenith-client";
import { FixNumber } from "../../../../common/orders";
import { getCurrencyShorthand } from "../../../../providers/StationProvider";
import EntityTable from "../../../common/tables/EntityTable";
import { FullTimeView } from "../../../common/core/Time";
import { OrderEnumsLists } from "../../../../common/constants";

const Query = {
  name: "unsettled_orders",
  and: [
    {
      path: "payment.terminal.settled",
      comparer: Compare.EQ,
      value: false
    },
    {
      path: "payment.terminal.confirmed",
      value: true,
      comparer: Compare.EQ,
    }
  ]
};

export default function UnsettledOrdersSection() {
  const { t } = useTranslation();
  const { tabId } = useParams();
  const navigate = useNavigate();

  const columns = useMemo(() => {
    return [
      {
        field: "payment.sender.name",
        headerName: t("entity:order:sender"),
        valueGetter: (_, row) => row.payment.sender?.name || "-",
        minWidth: 160,
        flex: 0.35,
      },
      {
        field: "payment.terminal.confirmedDate",
        headerName: t("common:tables:paymentConfirmed"),
        type: "dateTime",
        valueGetter: (_, row) => !!row.payment.terminal?.confirmedDate ? new Date(row.payment.terminal?.confirmedDate) : null,
        renderCell: (params) => <FullTimeView time={params.row.payment.terminal?.confirmedDate} />,
        flex: 0.25,
        minWidth: 160,
      },
      {
        field: "payment.method",
        headerName: t("entity:order:payment:method"),
        valueGetter: (_, row) => t(`order:enums:paymentMethod:${OrderEnumsLists.PaymentMethod[row.payment.method]}`),
        minWidth: 160
      },
      {
        field: "payment.calculatedTotal.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:payment:calculatedTotal"),
        valueGetter: (_, row) => Number(row.payment.calculatedTotal?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.row.payment.calculatedTotal?.value)} ${getCurrencyShorthand(t, params.row.payment.calculatedTotal?.currency)}`,
        minWidth: 120
      },
      {
        field: "number",
        type: "number",
        headerName: t("entity:order:number"),
        flex: 0
      },
      {
        field: "missionNumber",
        type: "number",
        headerName: t("common:tables:mission"),
        flex: 0
      },
      {
        field: "customer.name",
        headerName: t("entity:order:customer"),
        valueGetter: (_, row) => row.customer?.name || "-",
        minWidth: 160,
        flex: 0.35,
      },
      {
        field: "comment",
        headerName: t("entity:order:internalComment"),
        valueGetter: (_, row) => row.comment || "-",
        minWidth: 160,
        flex: 0.75
      },
    ]
  }, [t]);

  const onRowClick = useCallback((params, ev) => {
    navigate(!tabId ? `./unsettled/${params.row.missionId}/${params.id}` : `./${params.row.missionId}/${params.id}`, { relative: "path" });
  }, [navigate, tabId]);

  return (
    <EntityTable
      tableId="unsettled_orders"
      type="Order"
      filter={Query}
      onRowClick={onRowClick}
      columns={columns}
      emptyStateTitle={t("unsettledCashOrders:emptyStateTitle")}
      emptyStateDescription={t("unsettledCashOrders:emptyStateDescription")}
      serverSidePaging
      xs={12}
    />
  );
}

